import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Search, Pencil, Trash2, Filter } from "lucide-react";
import LeadFormDialog from "@/components/leads/LeadFormDialog";
import { useReference } from "@/lib/reference";

const STAGE_COLOR = {
  "New Lead": "bg-[#6B5944]/15 text-[#2A1810]",
  "Initial Contact": "bg-[#5D3A1F]/10 text-[#5D3A1F]",
  "Discovery Meeting": "bg-[#6B8E5A]/15 text-[#6B8E5A]",
  "Needs Assessment": "bg-[#D4A547]/15 text-[#A6741F]",
  "Proposal Drafting": "bg-[#C28A2E]/15 text-[#7A4A18]",
  "Proposal Sent": "bg-[#C28A2E]/20 text-[#7A4A18]",
  "Negotiation": "bg-[#D4A547]/20 text-[#A6741F]",
  "Won": "bg-[#5D3A1F] text-white",
  "Lost": "bg-[#A23E3E]/15 text-[#A23E3E]",
};

const KanbanCard = ({ lead, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`kanban-lead-${lead.id}`}
    className="ccs-card ccs-card-hover w-full text-left p-4 hover:border-[#5D3A1F]/40"
  >
    <div className="font-medium text-sm text-[#2A1810] truncate">{lead.organization_name}</div>
    <div className="text-xs text-[#6B5944] mt-0.5 truncate">{lead.contact_name}{lead.position ? ` · ${lead.position}` : ""}</div>
    <div className="flex items-center justify-between mt-3">
      <div className="flex items-center gap-1.5 flex-wrap">
        <Badge variant="outline" className="text-[10px] border-[#EBE3D2] text-[#6B5944]">{lead.lead_source}</Badge>
        {lead.workshop_interest && (
          <Badge className="text-[11px] bg-[#C28A2E]/15 text-[#7A541A] hover:bg-[#C28A2E]/15" data-testid={`lead-workshop-badge-${lead.id}`}>{lead.workshop_interest}</Badge>
        )}
      </div>
      {lead.estimated_value > 0 && (
        <div className="text-xs font-semibold text-[#5D3A1F]">${Number(lead.estimated_value).toLocaleString()}</div>
      )}
    </div>
  </button>
);

const LeadsPage = () => {
  const ref = useReference();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [workshopFilter, setWorkshopFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchLeads = () => {
    setLoading(true);
    api.get("/leads").then((r) => setLeads(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { fetchLeads(); }, []);

  const workshopNames = [...new Set(leads.map((l) => l.workshop_interest).filter(Boolean))];

  const filtered = leads.filter((l) => {
    if (stageFilter !== "all" && l.stage !== stageFilter) return false;
    if (workshopFilter !== "all" && l.workshop_interest !== workshopFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (l.organization_name || "").toLowerCase().includes(q) ||
      (l.contact_name || "").toLowerCase().includes(q) ||
      (l.email || "").toLowerCase().includes(q) ||
      (l.workshop_interest || "").toLowerCase().includes(q);
  });

  const handleCreate = async (data) => {
    try {
      await api.post("/leads", data);
      toast.success("Lead added");
      fetchLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add lead");
      throw e;
    }
  };

  const handleUpdate = async (data) => {
    try {
      await api.put(`/leads/${editing.id}`, data);
      toast.success("Lead updated");
      fetchLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update lead");
      throw e;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/leads/${id}`);
    toast.success("Lead deleted");
    fetchLeads();
  };

  const openEdit = (lead) => { setEditing(lead); setDialogOpen(true); };
  const openCreate = () => { setEditing(null); setDialogOpen(true); };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Pipeline</div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight mt-1.5 text-[#2A1810]">Leads & Opportunities</h1>
          <p className="text-[#6B5944] mt-1.5">Track every opportunity from first contact to engagement.</p>
        </div>
        <Button onClick={openCreate} data-testid="add-lead-btn" className="bg-[#C28A2E] hover:bg-[#A6741F] text-white rounded-xl h-11 px-5 hover:-translate-y-[1px] transition-all">
          <Plus className="w-4 h-4 mr-2" /> Add lead
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B5944]" />
          <Input
            data-testid="leads-search-input"
            placeholder="Search by organization, contact or email…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-11 rounded-xl bg-white border-[#EBE3D2]"
          />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger data-testid="leads-stage-filter" className="md:w-[220px] h-11 rounded-xl bg-white border-[#EBE3D2]">
            <Filter className="w-4 h-4 mr-2 text-[#6B5944]" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {ref.stages.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
          </SelectContent>
        </Select>
        {workshopNames.length > 0 && (
          <Select value={workshopFilter} onValueChange={setWorkshopFilter}>
            <SelectTrigger data-testid="leads-workshop-filter" className="md:w-[220px] h-11 rounded-xl bg-white border-[#EBE3D2]">
              <Filter className="w-4 h-4 mr-2 text-[#6B5944]" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All workshops</SelectItem>
              {workshopNames.map((w) => (<SelectItem key={w} value={w}>{w}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
      </div>

      <Tabs defaultValue="kanban" className="w-full">
        <TabsList className="bg-[#EBE3D2]/50 rounded-xl">
          <TabsTrigger value="kanban" data-testid="tab-kanban" className="rounded-lg">Pipeline</TabsTrigger>
          <TabsTrigger value="table" data-testid="tab-table" className="rounded-lg">Table</TabsTrigger>
        </TabsList>

        <TabsContent value="kanban" className="mt-5">
          {loading ? (
            <div className="text-center py-16 text-[#6B5944]">Loading…</div>
          ) : (
            <div className="overflow-x-auto pb-3">
              <div className="grid grid-flow-col auto-cols-[280px] gap-4">
                {ref.stages.map((stage) => {
                  const stageLeads = filtered.filter((l) => l.stage === stage);
                  return (
                    <div key={stage} className="rounded-2xl p-3 bg-[#EBE3D2]/30 border border-[#EBE3D2]" data-testid={`stage-col-${stage}`}>
                      <div className="flex items-center justify-between px-2 pb-3">
                        <div className="text-xs font-semibold text-[#2A1810]">{stage}</div>
                        <Badge variant="secondary" className="bg-white text-[#6B5944] text-[10px]">{stageLeads.length}</Badge>
                      </div>
                      <div className="space-y-2 min-h-[80px]">
                        {stageLeads.map((l) => (<KanbanCard key={l.id} lead={l} onClick={() => openEdit(l)} />))}
                        {stageLeads.length === 0 && (
                          <div className="text-xs text-[#6B5944]/60 text-center py-6">No leads in this stage</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="table" className="mt-5">
          <div className="ccs-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-[#EBE3D2] hover:bg-transparent">
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944] py-4">Organization</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944]">Contact</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944]">Stage</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944]">Source</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944] text-right">Value</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((l) => (
                  <TableRow key={l.id} className="border-[#EBE3D2]" data-testid={`lead-row-${l.id}`}>
                    <TableCell className="py-4">
                      <div className="font-medium text-[#2A1810]">{l.organization_name}</div>
                      <div className="text-xs text-[#6B5944]">{l.industry}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-[#2A1810]">{l.contact_name}</div>
                      <div className="text-xs text-[#6B5944]">{l.email}</div>
                    </TableCell>
                    <TableCell><Badge className={`${STAGE_COLOR[l.stage] || ""} rounded-full font-medium px-3`}>{l.stage}</Badge></TableCell>
                    <TableCell className="text-sm text-[#6B5944]">
                      {l.lead_source}
                      {l.workshop_interest && <span className="block text-[11px] text-[#7A541A] font-semibold">{l.workshop_interest}</span>}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-[#5D3A1F]">${Number(l.estimated_value || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(l)} data-testid={`edit-lead-${l.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(l.id)} data-testid={`delete-lead-${l.id}`}>
                        <Trash2 className="w-4 h-4 text-[#A23E3E]" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && !loading && (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#6B5944]">No leads found. Click "Add lead" to get started.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <LeadFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        mode={editing ? "edit" : "create"}
        onSubmit={editing ? handleUpdate : handleCreate}
        onConverted={() => { fetchLeads(); }}
      />
    </div>
  );
};

export default LeadsPage;
